import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Info, BellOff, Bell, AlertTriangle } from 'lucide-react';
import { metrics, alerts as alertApi, type Zeitraum, type Alarm } from '../api';
import Verlaufsgraph from './Verlaufsgraph';

/**
 * Verläufe und offene Alarme zu einem Objekt.
 *
 * WARUM BEIDES ZUSAMMEN: Ein Graph beantwortet „wie war es", ein Alarm „was
 * stimmt gerade nicht". Wer das eine ansieht, will meistens auch das andere —
 * und ein Alarm, der nur auf einer eigenen Seite steht, wird beim Nachsehen
 * übersehen.
 *
 * DIE ZEITRÄUME sind fest vorgegeben, nicht frei wählbar. Der PC schickt kein
 * PromQL ans Backend; er nennt eine Ansicht und einen Zeitraum. Ab 30 Tagen
 * antwortet der Verlaufsspeicher mit Stundenwerten statt der Rohspeicher mit
 * Minutenwerten — das steht auch dran, damit niemand sich über die gröbere
 * Linie wundert.
 */

const ZEITRAEUME: Array<{ wert: Zeitraum; label: string }> = [
  { wert: '1h', label: '1 h' },
  { wert: '6h', label: '6 h' },
  { wert: '24h', label: '24 h' },
  { wert: '7d', label: '7 T' },
  { wert: '30d', label: '30 T' },
  { wert: '90d', label: '90 T' },
  { wert: '1y', label: '1 J' },
  { wert: '3y', label: '3 J' },
];

const SCHWERE_STIL: Record<string, string> = {
  critical: 'border-red-300 bg-red-50 text-red-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-800',
  info: 'border-slate-300 bg-slate-50 text-slate-700',
};

function AlarmZeile({ alarm, aufFrisch }: { alarm: Alarm; aufFrisch: () => void }) {
  const { t } = useTranslation();
  const stumm = alarm.silencedUntil && new Date(alarm.silencedUntil).getTime() > Date.now();

  const stummschalten = useMutation({
    mutationFn: () => alertApi.silence(alarm.id, 4),
    onSuccess: aufFrisch,
  });
  const aufheben = useMutation({
    mutationFn: () => alertApi.unsilence(alarm.id),
    onSuccess: aufFrisch,
  });

  return (
    <li className={`rounded border px-2.5 py-2 text-xs ${SCHWERE_STIL[alarm.severity] ?? SCHWERE_STIL.info}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{alarm.summary}</p>
          {alarm.description && <p className="mt-0.5 opacity-80">{alarm.description}</p>}
          <p className="mt-0.5 opacity-60">
            {t('metrics.since', {
              when: new Date(alarm.startsAt).toLocaleString('de-DE'),
            })}
            {stumm &&
              ` · ${t('metrics.silencedUntil', {
                when: new Date(alarm.silencedUntil!).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}`}
          </p>
        </div>
        <button
          onClick={() => (stumm ? aufheben.mutate() : stummschalten.mutate())}
          title={stumm ? t('metrics.unsilence') : t('metrics.silenceHint')}
          className="inline-flex shrink-0 items-center gap-1 opacity-70 hover:opacity-100"
        >
          {stumm ? <Bell size={12} /> : <BellOff size={12} />}
          {stumm ? t('metrics.unsilence') : t('metrics.silence')}
        </button>
      </div>
    </li>
  );
}

interface Props {
  /** Genau eines von beiden — oder keines für die Inferenz. */
  instanceId?: string;
  serverId?: string;
  titel: string;
  /** Gesetzt heißt: als Fenster darstellen, wie die übrigen Panels. */
  onClose?: () => void;
  /** Speichergrenze in Bytes, als gestrichelte Linie im Speichergraphen. */
  speichergrenze?: number | null;
  /**
   * Nur Alarme, deren Regelname so beginnt.
   *
   * Ohne das zeigt die Inferenzansicht auch „Wirt Platte voll" — richtig
   * abgefragt (offene Alarme ohne Objektbezug), aber an dieser Stelle Rauschen.
   * Wirt- und Instanzalarme haben ein Objekt und filtern sich selbst; die
   * Inferenzregeln haben keines und brauchen deshalb diesen Griff.
   */
  alarmPraefix?: string;
}

export default function MetrikPanel({
  instanceId,
  serverId,
  titel,
  onClose,
  speichergrenze,
  alarmPraefix,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [zeitraum, setZeitraum] = useState<Zeitraum>('24h');

  const schluessel = ['metrics', instanceId ?? serverId ?? 'inference', zeitraum];
  const { data, isLoading } = useQuery({
    queryKey: schluessel,
    queryFn: () =>
      instanceId
        ? metrics.instance(instanceId, zeitraum)
        : serverId
          ? metrics.server(serverId, zeitraum)
          : metrics.inference(zeitraum),
    // Auf Abruf, nicht im Takt: die Erhebung läuft durchgehend, das Zeichnen
    // nicht. Ein Intervall hier würde jede offene Seite zu einem Dauerabruf
    // machen, ohne dass jemand hinsieht.
    refetchOnWindowFocus: false,
  });

  const alarmSchluessel = ['alerts', instanceId ?? serverId ?? 'alle'];
  const { data: alarmDaten } = useQuery({
    queryKey: alarmSchluessel,
    queryFn: () => alertApi.list({ instanceId, serverId, offen: true }),
    refetchOnWindowFocus: false,
  });

  const offene = (alarmDaten?.alerts ?? []).filter(
    (a) => !alarmPraefix || a.name.startsWith(alarmPraefix),
  );
  const inhalt = (
    <>
      {/* Alarme zuerst. Wer hier landet, weil etwas nicht stimmt, soll nicht
          erst an drei Graphen vorbeiscrollen. */}
      {offene.length > 0 && (
        <ul className="mb-3 space-y-1.5">
          {offene.map((a) => (
            <AlarmZeile
              key={a.id}
              alarm={a}
              aufFrisch={() => qc.invalidateQueries({ queryKey: alarmSchluessel })}
            />
          ))}
        </ul>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-1">
        {ZEITRAEUME.map((z) => (
          <button
            key={z.wert}
            onClick={() => setZeitraum(z.wert)}
            className={`rounded px-2 py-1 text-xs font-medium ${
              zeitraum === z.wert
                ? 'bg-brand-600 text-white'
                : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {z.label}
          </button>
        ))}
        {data?.zustand.speicher === 'verlauf' && (
          <span className="ml-1 text-xs text-slate-400">{t('metrics.fromHistory')}</span>
        )}
      </div>

      {isLoading ? (
        <div className="h-32 animate-pulse rounded bg-slate-100" />
      ) : !data?.zustand.verfuegbar ? (
        // Der Grund im Klartext, nicht ein leerer Kasten. Dieselbe Regel wie
        // bei den Snapshots.
        <div className="flex gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
          <div>
            <p className="mb-0.5 font-medium text-slate-700">{t('metrics.unavailable')}</p>
            <p>{data?.zustand.grund}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {data.reihen.map((reihe) => (
            <Verlaufsgraph
              key={reihe.key}
              reihe={reihe}
              grenze={reihe.key === 'memory' && instanceId ? (speichergrenze ?? null) : null}
              grenzeLabel={t('metrics.limit')}
            />
          ))}
        </div>
      )}
    </>
  );

  if (!onClose) {
    return (
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {titel}
        </h4>
        {inhalt}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-y-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('metrics.title')}</h2>
            <p className="font-mono text-xs text-slate-500">{titel}</p>
          </div>
          <div className="flex items-center gap-2">
            {offene.length > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-red-700">
                <AlertTriangle size={12} /> {t('metrics.openAlerts', { count: offene.length })}
              </span>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="px-6 py-4">{inhalt}</div>
      </div>
    </div>
  );
}
